/**
 * «Avisarme por email cuando termine»: the switch of every launch, and what is said after it.
 *
 * Virtual Profiler sends the email, to the operator's own address, once the execution leaves its
 * measurement: its figures and a link back here. The back-office only asks, right after the launch
 * — the label of the execution only exists once it is launched — with the page the link opens: the
 * group, showing that execution, as «Ver» opens it.
 *
 * Asking is never allowed to fail the launch: the analyses are queued by then, and a launch that
 * read as failed would be launched again. A request that fails is said beside the launch instead.
 */
import type { RouteLocationRaw } from 'vue-router';
import type { ExecutionSubscription, GroupOwner, GroupRunResult } from '../model.js';
import { controlDetailLocation } from './control-detail.js';
import { isControlOwner } from './group-owner.js';

export const EMAIL_SWITCH_LABEL = 'Avisarme por email cuando termine';

/**
 * The owner's page, showing that execution's videos: where «Ver» takes it, so the link reads the
 * same. A control's execution opens the control's page, by its kind — a control has no group id.
 */
export function executionResultLocation(owner: GroupOwner, batch: string): RouteLocationRaw {
  if (isControlOwner(owner)) {
    return {
      ...controlDetailLocation(owner.controlKind),
      query: { tab: 'videos', batch },
    };
  }

  return {
    name: 'virtual-profiler-group-detail',
    params: { groupId: String(owner.groupId) },
    query: { tab: 'videos', batch },
  };
}

/** What asking for the email came to; null when nobody asked or nothing was launched. */
export type EmailRequest =
  { kind: 'subscribed'; subscription: ExecutionSubscription } | { kind: 'failed'; error: unknown };

export type LaunchWithEmail = GroupRunResult & { email: EmailRequest | null };

/**
 * Launches, then asks for the email of what was launched.
 *
 * Nothing accepted is no execution — there is nothing to be told about — so nothing is asked;
 * unless it waits its turn (`queued`): it has no runs yet, but it will start and finish, and the
 * email goes then. The launch's own failure is the caller's, untouched; the email's never escapes.
 */
export async function launchWithEmail(
  launch: () => Promise<GroupRunResult>,
  subscribe: ((batch: string) => Promise<ExecutionSubscription>) | null,
): Promise<LaunchWithEmail> {
  const result = await launch();

  if (!subscribe || (result.accepted.length === 0 && !result.queued)) {
    return { ...result, email: null };
  }

  try {
    return {
      ...result,
      email: {
        kind: 'subscribed',
        subscription: await subscribe(result.batch),
      },
    };
  } catch (error) {
    return { ...result, email: { kind: 'failed', error } };
  }
}

/** What the launch's toast adds about the email; null when there is nothing to add. */
export function emailNote(
  request: EmailRequest | null,
): { tone: 'info' | 'error'; text: string } | null {
  if (!request) return null;
  if (request.kind === 'failed') {
    return {
      tone: 'error',
      text: 'No se ha podido activar el aviso por email.',
    };
  }

  const { email, status } = request.subscription;

  switch (status) {
    case 'pending':
      return { tone: 'info', text: `Te avisaremos a ${email} cuando termine.` };
    case 'sent':
      return {
        tone: 'info',
        text: `Ya había terminado: te lo hemos enviado a ${email}.`,
      };
    case 'dropped':
      return {
        tone: 'info',
        text: 'No habrá email: la ejecución se ha cancelado.',
      };
    case 'failed':
      return { tone: 'error', text: 'El email no se ha podido enviar.' };
    default:
      return null;
  }
}

/** The launch's toast: its own message, the email beside it, and a warning when the email failed. */
export function launchToast(
  message: string,
  request: EmailRequest | null,
): {
  tone: 'success' | 'warning';
  message: string;
  description: string | undefined;
} {
  const note = emailNote(request);

  return {
    tone: note?.tone === 'error' ? 'warning' : 'success',
    message,
    description: note?.text,
  };
}

/** Several groups launched in turn: one sentence about all their emails. */
export function bulkEmailNote(requests: readonly (EmailRequest | null)[]): string | undefined {
  const asked = requests.filter((request): request is EmailRequest => request !== null);
  const failed = asked.filter((request) => request.kind === 'failed').length;

  if (asked.length === 0) return undefined;
  if (failed === 0) {
    return asked.length === 1
      ? 'Te avisaremos por email cuando termine.'
      : 'Te avisaremos por email cuando termine cada una.';
  }

  return failed === asked.length
    ? 'No se ha podido activar el aviso por email.'
    : `No se ha podido activar el aviso por email de ${failed} de ${asked.length}.`;
}
